import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/content';
const AUTOPLAY_MS = 8000;
export function PreviewCarousel() {
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const isInView = useInView(containerRef, {
    amount: 0.4
  });
  const reduceMotion = useReducedMotion();
  const total = previewPages.length;
  const go = useCallback((direction: number) => setIndex(current => (current + direction + total) % total), [total]);

  // Autoplay only while the carousel is on screen; resets whenever the slide changes.
  useEffect(() => {
    if (!isInView) return;
    const timer = window.setTimeout(() => go(1), AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [index, isInView, go]);

  // Preload the next page so transitions never show a blank frame.
  useEffect(() => {
    const next = previewPages[(index + 1) % total];
    const img = new Image();
    img.src = next.src;
  }, [index, total]);
  const page = previewPages[index];
  return <div ref={containerRef} className="w-full" role="region" aria-roledescription="carousel" aria-label="Pages from inside THE COMPLETE ART YEAR™" data-id="element-148" data-mp="3fjdx6a7wmjmf">
      <div className="w-full overflow-hidden rounded-xl border-2 border-gold bg-[#F7F1E6]" data-id="element-149" data-mp="3ocikm5c6di7c">
        <div className="relative aspect-[1000/1294] w-full touch-pan-y" onTouchStart={event => {
        touchStartX.current = event.touches[0].clientX;
      }} onTouchEnd={event => {
        if (touchStartX.current === null) return;
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
      }} data-id="element-150" data-mp="3tblovnkbzke9">
          <AnimatePresence initial={false} data-id="element-151" data-mp="20vao06lii4fn">
            <motion.img key={page.src} src={page.src} alt={page.alt} width={1000} height={1294} loading={index === 0 ? 'eager' : 'lazy'} decoding="async" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: reduceMotion ? 0 : 0.25,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute inset-0 h-full w-full object-contain" data-id="element-152" data-mp="20wcbjajpsje9" />
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3" data-id="element-153" data-mp="2tjzruddycnv3">
        <button type="button" onClick={() => go(-1)} aria-label="Previous page" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-150 ease-out hover:bg-gold/10" data-id="element-154" data-mp="2ompvsew25dya">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-155" data-mp="3h97qao8vctig" />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-1" data-id="element-156" data-mp="3m7rbgmfn219r">
          {previewPages.map((item, itemIndex) => <button key={item.src} type="button" onClick={() => setIndex(itemIndex)} aria-label={`Show page ${itemIndex + 1} of ${total}`} aria-current={itemIndex === index} className="group flex h-6 flex-1 items-center" data-id="element-157" data-mp="386f1crn5stvs" data-mpi={itemIndex}>
              <span className={`block h-1 w-full rounded-full transition-colors duration-200 ease-out ${itemIndex === index ? 'bg-gold' : 'bg-gold/25 group-hover:bg-gold/50'}`} data-id="element-158" data-mp="339ydlyjvghj8" />
            </button>)}
        </div>

        <span className="shrink-0 text-xs font-medium tabular-nums text-white/60" aria-live="polite" data-id="element-159" data-mp="2fjb1f12xb5qv">
          {index + 1} / {total}
        </span>

        <button type="button" onClick={() => go(1)} aria-label="Next page" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-150 ease-out hover:bg-gold/10" data-id="element-160" data-mp="3mv225y1tuuhm">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-161" data-mp="393wvwd3yf3yb" />
        </button>
      </div>
    </div>;
}